import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, Row, Col, Typography, Descriptions, Tag, Button,
  Table, Statistic, Space, message, Select, Tabs
} from 'antd';
import PujaSpinner from '../../components/PujaSpinner';
import TagCategoria from '../../components/TagCategoria';
import { fecha, money } from '../../utils/format';
import { descargarBlob } from '../../utils/descargas';
import {
  EditOutlined,
  UploadOutlined,
  ArrowLeftOutlined,
  UnorderedListOutlined,
  DollarOutlined,
  FileExcelOutlined,
} from '@ant-design/icons';
import BotonBloqueado from '../../components/BotonBloqueado';
import { useAuth } from '../../context/AuthContext';
import { eventoService, categoriaService, ofertaService } from '../../services/api';

const { Title } = Typography;
const { Option } = Select;

const EventoDetalle = () => {
  const { user, tieneRecurso } = useAuth();

  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [evento, setEvento] = useState(null);
  /* ¿Este remate lo organizo yo, o me invitaron a consignar?
   *
   * Va DESPUES de declarar `evento`, obviamente. Y como arranca en null
   * mientras carga, esto da false y la pantalla muestra el boton bloqueado:
   * ante la duda no se ofrece escribir sobre algo que puede no ser propio. */
  // Explicito y no `a?.x === b?.x`: mientras cargan, ese atajo compara
  // undefined con undefined y da TRUE, o sea que por un instante ofrece
  // escribir sobre algo que puede no ser propio. Hoy lo tapa el early
  // return de `!evento`, pero no puede depender de eso.
  const esPropio = Boolean(evento && user && evento.id_clientes === user.id_clientes);
  /* Mismos dos ejes que en la grilla, con la misma precedencia: la
   * propiedad primero, porque ningun plan arregla que el remate sea de
   * otro. El eje del plan cubre la suscripcion vencida, que conserva
   * `ver_eventos` y pierde `crud_eventos`. */
  const motivoBloqueo = !esPropio ? 'ajeno'
    : !tieneRecurso('crud_eventos') ? 'plan'
    : null;
  const [animales, setAnimales] = useState([]);
  const [estadisticas, setEstadisticas] = useState(null);
  // Categorias con animales en este evento (para filtro de tab Animales).
  // Un solo flag para los dos Selects, a diferencia de AnimalesList: aca las
  // dos listas se piden en UN `Promise.all`, asi que llegan juntas. Alla eran
  // requests independientes y una podia volver mucho antes que la otra.
  const [loadingCategorias, setLoadingCategorias] = useState(true);
  const [categoriasConAnimales, setCategoriasConAnimales] = useState([]);
  // Categorias con ofertas en este evento (para filtro de tab Ofertas).
  const [categoriasConOfertas, setCategoriasConOfertas] = useState([]);
  const [filtroCategoria, setFiltroCategoria] = useState(null);
  const [ofertas, setOfertas] = useState([]);
  const [loadingOfertas, setLoadingOfertas] = useState(false);
  const [filtroCategoriaOferta, setFiltroCategoriaOferta] = useState(null);
  const [exportingExcel, setExportingExcel] = useState(false);

  // Filtrado client-side de ofertas por categoria del animal.
  // getByEvento trae hasta 500 ofertas de una, asi que no hay que ir al backend.
  const ofertasFiltradas = useMemo(() => {
    if (!filtroCategoriaOferta) return ofertas;
    return ofertas.filter(o => o.animal?.categoria?.id === filtroCategoriaOferta);
  }, [ofertas, filtroCategoriaOferta]);

  useEffect(() => {
    fetchEvento();
    fetchEstadisticas();
    fetchCategorias();
    fetchOfertas();
    // fetchAnimales NO va aca - el siguiente useEffect lo dispara al mount
    // (filtroCategoria empieza en null, asi que tambien corre la primera vez)
  }, [id]);

  useEffect(() => {
    fetchAnimales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, filtroCategoria]);

  const fetchEvento = async () => {
    try {
      const response = await eventoService.getById(id);
      setEvento(response.data);
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al cargar evento');
      navigate('/eventos');
    }
  };

  const fetchAnimales = async () => {
    setLoading(true);
    try {
      const params = filtroCategoria ? { categoria: filtroCategoria } : {};
      const response = await eventoService.getAnimales(id, params);
      setAnimales(response.data.animales || []);
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al cargar animales del evento');
    } finally {
      setLoading(false);
    }
  };

  const fetchEstadisticas = async () => {
    try {
      const response = await eventoService.getEstadisticas(id);
      setEstadisticas(response.data.estadisticas);
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al cargar estadísticas');
    }
  };

  const fetchCategorias = async () => {
    setLoadingCategorias(true);
    try {
      // Cargamos en paralelo los dos cortes que usan los filtros: el de
      // animales del evento (para el tab Animales) y el de ofertas del
      // evento (para el tab Ofertas). Cada filtro muestra solo las
      // categorias que efectivamente van a devolver resultados.
      const [conAnimales, conOfertas] = await Promise.all([
        categoriaService.getConAnimalesEnEvento(id),
        categoriaService.getConOfertasEnEvento(id),
      ]);
      setCategoriasConAnimales(Array.isArray(conAnimales.data) ? conAnimales.data : []);
      setCategoriasConOfertas(Array.isArray(conOfertas.data) ? conOfertas.data : []);
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al cargar categorías');
    } finally {
      setLoadingCategorias(false);
    }
  };

  const handleExportarGanadoresExcel = async () => {
    setExportingExcel(true);
    try {
      const response = await eventoService.exportarGanadoresExcel(id);
      const filename = `ganadores_${evento?.slug || id}.xlsx`;
      // El compartido y no una copia local: la de aca revocaba el objectURL en
      // el MISMO TICK, que es el patron que GenerarPDF ya habia descartado
      // porque puede matar una descarga grande a mitad de camino. Ademas se
      // llamaba igual que el exportado, con otra firma — el proximo que
      // "limpiara" agregando el import rompia la descarga.
      descargarBlob(response, filename);
      message.success('Excel descargado');
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al exportar Excel');
    } finally {
      setExportingExcel(false);
    }
  };

  const fetchOfertas = async () => {
    setLoadingOfertas(true);
    try {
      const response = await ofertaService.getByEvento(id);
      setOfertas(response.data.ofertas || []);
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al cargar ofertas');
    } finally {
      setLoadingOfertas(false);
    }
  };

  const animalesColumns = [
    {
      title: 'Corral',
      dataIndex: 'corral',
      key: 'corral',
      width: 80,
      sorter: (a, b) => a.corral - b.corral,
    },
    {
      title: 'Nombre',
      dataIndex: 'nom_animal',
      key: 'nom_animal',
      render: (text, record) => (
        <a onClick={() => navigate(`/animales/${record.id_animal}`)}>{text}</a>
      ),
    },
    {
      title: 'RP',
      dataIndex: 'rp_animal',
      key: 'rp_animal',
    },
    {
      title: 'Categoría',
      key: 'categoria',
      render: (_, record) => <TagCategoria valor={record.categoria?.nom_categoria} />,
    },
    {
      title: 'Precio Base',
      dataIndex: 'precio_base',
      key: 'precio_base',
      render: (v) => money(v),
    },
  ];

  if (!evento) {
    return (
      <PujaSpinner size="lg" />
    );
  }

  return (
    <div>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/eventos')}>
              Volver
            </Button>
            <Title level={2} style={{ margin: 0 }}>
              {evento.nombre_evento}
            </Title>
            <Tag color={evento.estado_evento === 1 ? 'green' : 'red'}>
              {evento.estado_evento === 1 ? 'Activo' : 'Inactivo'}
            </Tag>
          </Space>
        </Col>
        <Col>
          {!motivoBloqueo ? (
            <Space>
              {/* La carga masiva DEL REMATE. Es la unica via por la que los
                  animales de una cabaña invitada pueden nacer a su nombre: la
                  columna CABAÑA del CSV nombra la cabaña del animal (su codigo
                  corto o su nombre), y sin evento no hay contra que validarla.
                  Detras de su propio recurso, que es un plan distinto de
                  `crud_eventos`. */}
              {tieneRecurso('carga_masiva_animales') && (
                <Button
                  icon={<UploadOutlined />}
                  onClick={() => navigate(`/eventos/${id}/carga-masiva`)}
                >
                  Cargar animales
                </Button>
              )}
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={() => navigate(`/eventos/${id}/editar`)}
              >
                Editar
              </Button>
            </Space>
          ) : (
            /* EL CASO DESTRUCTIVO, no un boton muerto.
             *
             * Esta pantalla ahora tambien la abre la cabania INVITADA, porque el
             * remate donde consigno le aparece en su panel. Con el boton libre,
             * navegaba a /editar, el formulario cargaba COMPLETO —la lectura del
             * evento si esta abierta para el invitado— llenaba todo, apretaba
             * Guardar y recien ahi el PUT devolvia 403. Perdia el trabajo.
             *
             * Que la lectura este abierta y la escritura cerrada es correcto; lo
             * que faltaba era que la UI supiera cual es cual. */
            <BotonBloqueado motivo={motivoBloqueo} type="primary" icon={<EditOutlined />}>
              Editar
            </BotonBloqueado>
          )}
        </Col>
      </Row>

      {/* Estadísticas */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic 
              title="Total Animales" 
              value={estadisticas?.total_animales || 0} 
              prefix={<UnorderedListOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic 
              title="Total Ofertas" 
              value={estadisticas?.total_ofertas || 0} 
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic 
              title="Fecha Inicio" 
              value={fecha(evento.fecha_inicio)} 
              valueStyle={{ fontSize: 18 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic 
              title="Fecha Fin" 
              value={fecha(evento.fecha_fin)}
              valueStyle={{ fontSize: 18 }}
            />
          </Card>
        </Col>
      </Row>

      {/* Info del evento */}
      <Row gutter={24} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <Card title="Información del Evento">
            <Descriptions column={1}>
              <Descriptions.Item label="Cliente">
                {evento.cliente?.razon_social || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Horario">
                {evento.hora_inicio && evento.hora_fin 
                  ? `${evento.hora_inicio} - ${evento.hora_fin}` 
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Envío Email">
                {evento.envio_email ? 'Sí' : 'No'}
              </Descriptions.Item>
              <Descriptions.Item label="Mostrar Ganadores">
                {evento.muestra_ganadores ? 'Sí' : 'No'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Por Categoría">
            {estadisticas?.por_categoria?.length > 0 ? (
              <Row gutter={[16, 16]}>
                {estadisticas.por_categoria.map((cat, index) => (
                  <Col xs={24} sm={12} key={index}>
                    <Statistic 
                      title={cat.categoria} 
                      value={cat.cantidad}
                      valueStyle={{ fontSize: 24 }}
                    />
                  </Col>
                ))}
              </Row>
            ) : (
              <p style={{ color: '#999' }}>Sin animales por categoría</p>
            )}
          </Card>
        </Col>
      </Row>

      {/* Tabs: Animales / Ofertas */}
      <Tabs
        defaultActiveKey="animales"
        items={[
          {
            key: 'animales',
            label: <span><UnorderedListOutlined /> Animales ({animales.length})</span>,
            children: (
              <Card
                variant="borderless"
                extra={
                  <Select
                    placeholder="Filtrar por categoría"
                    style={{ width: 200 }}
                    loading={loadingCategorias}
                    allowClear
                    value={filtroCategoria}
                    onChange={setFiltroCategoria}
                  >
                    {categoriasConAnimales.map(cat => (
                      <Option key={cat.id_categoria} value={cat.id_categoria}>
                        {cat.nom_categoria}
                      </Option>
                    ))}
                  </Select>
                }
              >
                <Table
                  scroll={{ x: 'max-content' }}
                  columns={animalesColumns}
                  dataSource={animales}
                  rowKey="id_animal"
                  loading={loading}
                  pagination={{
                    showSizeChanger: true,
                    showTotal: (total) => `Total: ${total} animales`,
                  }}
                />
              </Card>
            ),
          },
          {
            key: 'ofertas',
            label: <span><DollarOutlined /> Ofertas ({ofertas.length})</span>,
            children: (
              <Card
                variant="borderless"
                extra={
                  <Space>
                    <Select
                      placeholder="Filtrar por categoría"
                      style={{ width: 220 }}
                      loading={loadingCategorias}
                      allowClear
                      value={filtroCategoriaOferta}
                      onChange={setFiltroCategoriaOferta}
                    >
                      {categoriasConOfertas.map(cat => (
                        <Option key={cat.id_categoria} value={cat.id_categoria}>
                          {cat.nom_categoria}
                        </Option>
                      ))}
                    </Select>
                    {esPropio && tieneRecurso('gestionar_ofertas') ? (
                      <Button
                        type="primary"
                        icon={<FileExcelOutlined />}
                        onClick={handleExportarGanadoresExcel}
                        loading={exportingExcel}
                        disabled={ofertas.length === 0}
                      >
                        Exportar ganadores
                      </Button>
                    ) : (
                      /* Este endpoint pide `gestionar_ofertas`, que un plan de
                       * solo lectura no incluye. Acá el motivo SI es el plan y
                       * no la propiedad: son dos conversaciones distintas. */
                      <BotonBloqueado
                        motivo={esPropio ? 'plan' : 'ajeno'}
                        type="primary"
                        icon={<FileExcelOutlined />}
                      >
                        Exportar ganadores
                      </BotonBloqueado>
                    )}
                  </Space>
                }
              >
                <Table
                  scroll={{ x: 'max-content' }}
                  columns={[
                    {
                      title: 'Corral',
                      dataIndex: 'corral_oferta',
                      key: 'corral_oferta',
                      width: 80,
                      sorter: (a, b) => a.corral_oferta - b.corral_oferta,
                    },
                    {
                      title: 'Animal',
                      key: 'animal',
                      render: (_, r) => r.animal
                        ? <a onClick={() => navigate(`/animales/${r.animal.id}`)}>{r.animal.nombre}</a>
                        : '-',
                    },
                    {
                      title: 'RP',
                      key: 'rp',
                      render: (_, r) => r.animal?.rp || '-',
                    },
                    {
                      title: 'Categoría',
                      key: 'categoria',
                      width: 160,
                      render: (_, r) => <TagCategoria valor={r.animal?.categoria?.nombre} />,
                      sorter: (a, b) => (a.animal?.categoria?.nombre || '').localeCompare(b.animal?.categoria?.nombre || ''),
                    },
                    {
                      title: 'Monto',
                      dataIndex: 'monto_oferta',
                      key: 'monto_oferta',
                      render: (v) => <strong>{money(v)}</strong>,
                      sorter: (a, b) => a.monto_oferta - b.monto_oferta,
                      defaultSortOrder: 'descend',
                    },
                    {
                      title: 'Ofertante',
                      key: 'usuario',
                      render: (_, r) => r.usuario?.nombre || '-',
                    },
                    {
                      title: 'Fecha',
                      key: 'fecha',
                      // La FECHA por `fecha()`; la hora se concatena aparte
                      // porque viene en su propio campo.
                      render: (_, r) => (r.fecha_oferta
                        ? `${fecha(r.fecha_oferta)} ${r.hora_oferta || ''}`.trim()
                        : '—'),
                    },
                  ]}
                  dataSource={ofertasFiltradas}
                  rowKey="id_oferta"
                  loading={loadingOfertas}
                  pagination={{
                    showSizeChanger: true,
                    showTotal: (total) => `Total: ${total} ofertas`,
                  }}
                />
              </Card>
            ),
          },
        ]}
      />
    </div>
  );
};

export default EventoDetalle;
